import { Module, MutationTree, ActionTree } from 'vuex';
import { RootStateInterface } from '@/store/rootState';
import { RecentMessage } from '@/modules/dashboard/models/RecentMessage';
import { DashboardApi } from '@/modules/dashboard/api/dashboard';
import { messageContentToPlainText } from '@/modules/messages/utils/contentSanitizer';

export interface MessagesStateInterface {
  recentChats: RecentMessage[];
  hasNext: boolean;
  count: number;
  isLoading: boolean;
}

function resolveUserContext(rootState: any): { userId: number | null; companyId: number | null } {
  const actingBrokerUserId = rootState.personalAssistants?.currentActingBrokerUser?.id ?? null;
  const isCompanyOwner = rootState.auth?.user?.isCompanyOwner ?? false;

  if (actingBrokerUserId) {
    return { userId: actingBrokerUserId, companyId: null };
  } else if (isCompanyOwner) {
    return { userId: null, companyId: rootState.brokers?.company?.id ?? null };
  } else {
    const rawId = rootState.auth?.user?.id;
    return { userId: rawId ? parseInt(rawId, 10) : null, companyId: null };
  }
}

function getRecencyTimestamp(chat: RecentMessage): number | null {
  const recencyKeys = ['lastCreated', 'createdAt', 'updatedAt', 'lastMessageCreatedAt', 'lastMessageAt', 'lastCreatedAt'];
  for (const key of recencyKeys) {
    const value = (chat as any)?.[key];
    if (!value) continue;
    const dateValue = typeof value === 'object' && value.date ? value.date : value;
    const timestamp = new Date(dateValue).getTime();
    if (!Number.isNaN(timestamp)) return timestamp;
  }
  return null;
}

function sortChatsByRecency(chats: RecentMessage[]): RecentMessage[] {
  return chats
    .map((chat, index) => ({ chat, index, timestamp: getRecencyTimestamp(chat) }))
    .sort((a, b) => {
      if (a.timestamp === null && b.timestamp === null) return a.index - b.index;
      if (a.timestamp === null) return 1;
      if (b.timestamp === null) return -1;
      if (a.timestamp === b.timestamp) return a.index - b.index;
      return b.timestamp - a.timestamp;
    })
    .map((entry) => entry.chat);
}

// Previews are truncated, so the markup is flattened first. Cutting raw HTML
// mid-tag leaves broken markup that cannot be rendered.
function truncateContent(chats: RecentMessage[]): RecentMessage[] {
  return chats.map((item) => {
    if (!item.content) {
      return item;
    }

    const text = messageContentToPlainText(item.content);

    return { ...item, content: text.length > 40 ? text.substr(0, 40) + '...' : text };
  });
}

export const mutations: MutationTree<MessagesStateInterface> = {
  setRecentChats(st, payload: RecentMessage[]) {
    st.recentChats = payload;
  },
  appendRecentChats(st, payload: RecentMessage[]) {
    st.recentChats = [...st.recentChats, ...payload];
  },
  setHasNext(st, payload: boolean) {
    st.hasNext = payload;
  },
  setCount(st, payload: number) {
    st.count = payload;
  },
  setLoading(st, payload: boolean) {
    st.isLoading = payload;
  },
  markChatRead(st, chatId: number) {
    st.recentChats = st.recentChats.map((chat) =>
      chat.chatId === chatId ? { ...chat, read: true } : chat,
    );
  },
};

export const actions: ActionTree<MessagesStateInterface, RootStateInterface> = {
  async loadRecentChats(
    { commit, rootState },
    { page = 0, recordsPerPage = 20, append = false }: { page?: number; recordsPerPage?: number; append?: boolean } = {},
  ) {
    const { userId, companyId } = resolveUserContext(rootState);
    if (!userId && !companyId) return;

    commit('setLoading', true);
    try {
      const actingBrokerId = rootState.personalAssistants?.currentActingBrokerId ?? null;
      const response = await new DashboardApi().recentMessages(
        userId,
        companyId,
        page,
        recordsPerPage,
        actingBrokerId,
      );
      const chats = truncateContent(sortChatsByRecency(response.data || []));

      if (append) {
        commit('appendRecentChats', chats);
      } else {
        commit('setRecentChats', chats);
      }
      commit('setHasNext', response.hasNext || false);
      commit('setCount', response.count || 0);
    } finally {
      commit('setLoading', false);
    }
  },
};

const namespaced = true;

export const messages: Module<MessagesStateInterface, RootStateInterface> = {
  namespaced,
  state: {
    recentChats: [],
    hasNext: false,
    count: 0,
    isLoading: false,
  },
  mutations,
  actions,
};
